import React, { useState, useRef, useEffect, useMemo } from "react";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const Dropdown = ({
  options,
  onSelect,
  imgUrl,
  icon = undefined,
  isShouldRotate = false,
  placeholder = "",
  isBig = true,
  initValue = undefined,
  isCitySelected = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [initVal, setInitVal] = useState();
  const [isUsedDefValue, setIsUsedDefValue] = useState(false);
  // const [filteredOptions, setFilteredOptions] = useState([]);
  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleSelect = (option) => {
    setSelected(option);
    setIsOpen(false);
    setSearchTerm("");
    setInitVal(undefined);
    onSelect(option);
  };

  // The club dropdown of the search screen holds ~900 options; without this the whole list is re-filtered
  // on every render, including renders that changed neither the options nor the search term.
  const filteredOptions = useMemo(
    () => options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [options, searchTerm]
  );

  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      setSearchTerm("");
      onSelect(null);

      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (options.length > 0 && !isUsedDefValue) {
      setSelected(options.find((el) => el?.value === initValue));
      setIsUsedDefValue(true);
    }
  }, [options]);

  useEffect(() => {
    if (isCitySelected === "") {
      setSelected(null);
    }
  }, [isCitySelected]);

  //test
  React.useEffect(() => {
    setSelected(null);
  }, [isCitySelected]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // `icon` (an element, e.g. an inline svg using currentColor) takes precedence
  // over `imgUrl` so a caller can have CSS control the glyph's colour. Both
  // carry the same classes, so all existing .icon-dropdown rules still apply.
  const iconClassName = `icon-dropdown ${isOpen && isShouldRotate ? "rotate-symbol" : ""}`;
  const iconNode = icon ? (
    <span className={iconClassName}>{icon}</span>
  ) : (
    <img className={iconClassName} alt="icon" src={imgUrl} />
  );

  // Mirrors the label ternary below: with neither an initial nor a selected
  // option, the text rendered is the placeholder, which V2 dims like a real one.
  const isPlaceholder = !initVal && !selected;

  return (
    <div className="dropdown" ref={dropdownRef}>
      {isOpen ? (
        <div className={`inputContainer ${isBig ? "inputContainer-big" : ""}`}>
          {iconNode}
          <input
            type="text"
            placeholder={selected ? selected.label : placeholder}
            className="input"
            ref={inputRef}
            onChange={(el) => setSearchTerm(el.target.value)}
          />
        </div>
      ) : (
        <div className={`dropdown-header ${selected ? "pr-40" : ""}`} onClick={toggleDropdown}>
          <span className={`dropdown-icon`}>{iconNode}</span>
          <p
            className={`dropdown-value${isPlaceholder ? " dropdown-value--placeholder" : ""}`}
            style={{
              width: "85%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              direction: "rtl",
            }}
          >
            {initVal ? initVal.label : selected ? selected.label : placeholder}
          </p>
          {selected ? (
            <IconButton
              className="dropdown-clear"
              aria-label="close"
              onClick={(e) => {
                setSelected(null);
                setIsOpen(false);
                setSearchTerm("");
                onSelect(null);
                e.stopPropagation();
              }}
              sx={(theme) => ({
                color: theme.palette.grey[500],
                position: "absolute",
                top: "5px",
                right: "0px",
              })}
            >
              <CloseIcon />
            </IconButton>
          ) : (
            ""
          )}
        </div>
      )}

      {isOpen && (
        <div className="dropdown-menu">
          <ul className="dropdown-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li className="dropdown-item" onClick={() => handleSelect(option)}>
                  {option.label}
                </li>
              ))
            ) : (
              <li className="dropdown-item">No results found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
